import { createAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { State } from "../../interfaces";
import {
  fetchShippers,
  fetchShipperRequests,
  fetchShippersPayments,
  fetchShippersCloudData,
} from "../../services/shippers";

const initialState = {
  shippers: null,
  shippersCloudData: null,
  shipper_requests: null,
  shipper_payments: null,
  status: {
    shippers: "idle",
    shippersCloudData: "idle",
    shipperRequests: "idle",
    shippersPayments: "idle",
  },
  map: {
    zoom: 11, // Initial zoom level
    mapCenter: { lat: -34.605425, lng: -58.381555 }, // Initial map centers at Obelisco
  },
};

export const setMapCenter = createAction(
  "shippers/setMapCenter",
  (center: { lat: number; lng: number }, zoom: number) => {
    return {
      payload: { center, zoom },
    };
  }
);

export const updateMapCenter = createAsyncThunk(
  "shippers/updateMapCenter",
  async (
    { center, zoom }: { center: { lat: number; lng: number }; zoom: number },
    { dispatch }
  ) => {
    dispatch(setMapCenter(center, zoom));
    return { center, zoom };
  }
);

export const setZoom = createAction("shippers/setZoom", (zoom: number) => {
  return {
    payload: { zoom },
  };
});

export const getShippers = createAsyncThunk<
  any,
  void,
  {
    state: State;
  }
>("shippers/fetch", async () => {
  const response: any = await fetchShippers();
  return response;
});
export const getShippersCloudData = createAsyncThunk<
  any,
  { cloudId: string },
  {
    state: State;
  }
>("shippersCloudData/fetch", async ({ cloudId }) => {
  const response: any = await fetchShippersCloudData(cloudId);
  return response;
});

export const getShipperRequests = createAsyncThunk<
  any,
  void,
  {
    state: State;
  }
>("shipperRequests/fetch", async () => {
  const response: any = await fetchShipperRequests();
  return response;
});

export const getShipperPayments = createAsyncThunk<
  any,
  void,
  {
    state: State;
  }
>("shipperPayments/fetch", async () => {
  const response: any = await fetchShippersPayments();

  return response;
});

export const shippersSlice = createSlice({
  name: "shippers",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(setMapCenter, (state, action) => {
        state.map.mapCenter = action.payload.center;
        state.map.zoom = action.payload.zoom;
      })
      .addCase(updateMapCenter.fulfilled, (state, action) => {
        state.map.mapCenter = action.payload.center;
        state.map.zoom = action.payload.zoom;
      })
      .addCase(setZoom, (state, action) => {
        state.map.zoom = action.payload.zoom;
      });

    builder
      .addCase(getShippers.pending, (state) => {
        return {
          ...state,
          status: { ...state.status, shippers: "loading" },
        };
      })
      .addCase(getShippers.fulfilled, (state, action) => {
        return {
          ...state,
          status: { ...state.status, shippers: "idle" },
          shippers: action.payload,
        };
      })
      .addCase(getShippersCloudData.pending, (state) => {
        return {
          ...state,
          status: { ...state.status, shippersCloudData: "loading" },
        };
      })
      .addCase(getShippersCloudData.fulfilled, (state, action) => {
        return {
          ...state,
          status: { ...state.status, shippersCloudData: "idle" },
          shippersCloudData: action.payload,
        };
      })
      .addCase(getShipperRequests.pending, (state) => {
        return {
          ...state,
          status: { ...state.status, shipperRequests: "loading" },
        };
      })
      .addCase(getShipperRequests.fulfilled, (state, action) => {
        return {
          ...state,
          status: { ...state.status, shipperRequests: "idle" },
          shipper_requests: action.payload,
        };
      })
      .addCase(getShipperPayments.pending, (state) => {
        return {
          ...state,
          status: { ...state.status, shippersPayments: "loading" },
        };
      })
      .addCase(getShipperPayments.fulfilled, (state, action) => {
        return {
          ...state,
          status: { ...state.status, shippersPayments: "idle" },
          shipper_payments: action.payload,
        };
      });
  },
});

export default shippersSlice.reducer;
